// Katalog awatarów. Identyfikator awatara to `<grupa>:<nazwa>` (zob. src/lib/look.ts); ten sam id zapisuje się na serwerze.
// Awatary to obrazki użytkownika w public/avatars/<grupa>-<nazwa>.webp (patch-31: 3 grupy po 6 — Dinozaury, Pokémon, Neutralne). Grupa = motyw aplikacji.
// Żeby podmienić obrazek na lepszy, wystarczy wgrać plik o tej samej nazwie (kwadrat, najlepiej 420×420 albo większy). Żeby dodać awatar: wpis poniżej + plik.
// Gdy obrazek się nie wczyta (np. brak pliku), kółko pokazuje emoji z pola `glyph`.
import type { Pair } from '../lib/i18n.ts';
import { DEFAULT_LOOK, cleanAvatarId } from '../lib/look.ts';
import type { Look, ThemeId } from '../lib/look.ts';
import type { AppCtx } from './ctx.ts';
import { h } from './dom.ts';
import { txAttr } from './i18n.ts';

export interface AvatarDef {
  /** Część identyfikatora po dwukropku. */
  name: string;
  /** Nazwa po polsku (panel rodzica pokazuje tylko polską). */
  label: string;
  /** Ta sama nazwa po angielsku (napisy dla dziecka idą za przełącznikiem EN/PL — zob. `avatarName`). */
  labelEn: string;
  /** Emoji pokazywane, gdy obrazek się nie wczytał. */
  glyph: string;
  /** `cover` — obrazek wypełnia kółko (kwadratowe grafiki z tłem); `contain` — postać na przezroczystym tle, na kolorowym kółku. */
  fit: 'cover' | 'contain';
  /** Kolor kółka pod postacią (dla `contain`). */
  bg: string;
}

export interface AvatarSet {
  id: ThemeId;
  /** Nazwa grupy w „Mój wygląd” po polsku. */
  title: string;
  /** Ta sama nazwa po angielsku (zob. `setTitle`). */
  titleEn: string;
  /** Znak przy nazwie grupy. */
  icon: string;
  avatars: readonly AvatarDef[];
}

const CUT = 'contain' as const;
const FULL = 'cover' as const;

export const AVATAR_SETS: readonly AvatarSet[] = [
  {
    id: 'dino',
    title: 'Dinozaury',
    titleEn: 'Dinosaurs',
    icon: '🦖',
    avatars: [
      { name: 'rex', label: 'Rex', labelEn: 'Rex', glyph: '🦖', fit: FULL, bg: '#9bc77a' },
      { name: 'longneck', label: 'Długoszyj', labelEn: 'Longneck', glyph: '🦕', fit: FULL, bg: '#8fd3c0' },
      { name: 'tricera', label: 'Trójróg', labelEn: 'Triceratops', glyph: '🦏', fit: CUT, bg: '#d3ebd9' },
      { name: 'stego', label: 'Stegozaur', labelEn: 'Stegosaurus', glyph: '🦎', fit: CUT, bg: '#d6e6fb' },
      { name: 'ptero', label: 'Pterodaktyl', labelEn: 'Pterodactyl', glyph: '🦅', fit: CUT, bg: '#ffe7a6' },
      { name: 'ptero-volt', label: 'Elektryczny pterodaktyl', labelEn: 'Electric pterodactyl', glyph: '⚡', fit: FULL, bg: '#20233a' },
    ],
  },
  {
    id: 'pokemon',
    title: 'Pokémon',
    titleEn: 'Pokémon',
    icon: '⚡',
    avatars: [
      { name: 'bulba', label: 'Bulbasaur', labelEn: 'Bulbasaur', glyph: '🌱', fit: FULL, bg: '#8fd3c0' },
      { name: 'charizard', label: 'Charizard', labelEn: 'Charizard', glyph: '🔥', fit: FULL, bg: '#f4b183' },
      { name: 'lucario', label: 'Lucario', labelEn: 'Lucario', glyph: '🐺', fit: FULL, bg: '#101a33' },
      { name: 'pikachu', label: 'Pikachu', labelEn: 'Pikachu', glyph: '⚡', fit: CUT, bg: '#d6e9ff' },
      { name: 'gengar', label: 'Gengar', labelEn: 'Gengar', glyph: '👻', fit: CUT, bg: '#ffe9b8' },
      { name: 'mew', label: 'Mew', labelEn: 'Mew', glyph: '🐱', fit: CUT, bg: '#d7efe7' },
    ],
  },
  {
    id: 'neutral',
    title: 'Neutralne',
    titleEn: 'Neutral',
    icon: '🧊',
    avatars: [
      { name: 'moss', label: 'Mszysta kostka', labelEn: 'Mossy cube', glyph: '🌿', fit: CUT, bg: '#e6f1cf' },
      { name: 'lava', label: 'Lawowa kostka', labelEn: 'Lava cube', glyph: '🌋', fit: CUT, bg: '#ffe4cc' },
      { name: 'amethyst', label: 'Ametystowa kostka', labelEn: 'Amethyst cube', glyph: '💎', fit: CUT, bg: '#e9e2ff' },
      { name: 'ice', label: 'Lodowa kostka', labelEn: 'Ice cube', glyph: '🧊', fit: CUT, bg: '#ffe3d1' },
      { name: 'sand', label: 'Piaskowa kostka', labelEn: 'Sand cube', glyph: '🏜️', fit: CUT, bg: '#d6ecf5' },
      { name: 'water', label: 'Wodna kostka', labelEn: 'Water cube', glyph: '🌊', fit: CUT, bg: '#ffe8b5' },
    ],
  },
];

/** Nazwa grupy awatarów w obu językach. */
export const setTitle = (set: AvatarSet): Pair => ({ en: set.titleEn, pl: set.title });

/** Nazwa awatara w obu językach. */
export const avatarName = (def: AvatarDef): Pair => ({ en: def.labelEn, pl: def.label });

export const setOf = (theme: ThemeId): AvatarSet => AVATAR_SETS.find((s) => s.id === theme) ?? (AVATAR_SETS.find((s) => s.id === 'neutral') as AvatarSet);

/** Awatar domyślny motywu: pierwszy z jego grupy. */
export const defaultAvatar = (theme: ThemeId): string => `${setOf(theme).id}:${setOf(theme).avatars[0]?.name ?? ''}`;

/** Adres obrazka awatara (względem strony; aplikacja używa ścieżek względnych, jak reszta zasobów). */
export const avatarSrc = (setId: string, name: string): string => `./avatars/${setId}-${name}.webp`;

export interface ResolvedAvatar {
  id: string;
  set: AvatarSet;
  def: AvatarDef;
}

/** Awatar z wyglądu profilu: pusty lub nieznany id → domyślny awatar motywu (nieznany nie psuje ekranu, np. dawny awatar z patcha-28 albo usunięty plik). */
export function resolveAvatar(look: Look): ResolvedAvatar {
  const id = cleanAvatarId(look.avatar) || defaultAvatar(look.theme);
  const [setId, name] = id.split(':') as [string, string];
  const set = AVATAR_SETS.find((s) => s.id === setId);
  const def = set?.avatars.find((a) => a.name === name);
  if (set && def) return { id, set, def };
  const fallbackSet = setOf(look.theme);
  const fallback = fallbackSet.avatars[0] as AvatarDef;
  return { id: `${fallbackSet.id}:${fallback.name}`, set: fallbackSet, def: fallback };
}

export type AvatarSize = 'sm' | 'md' | 'lg';

/** Awatar jako element (kółko z obrazkiem). Z `ctx` opis dla czytnika ekranu idzie za przełącznikiem EN/PL; bez niego jest po polsku (panel rodzica). */
export function avatarEl(look: Look = DEFAULT_LOOK, size: AvatarSize = 'md', ctx?: Pick<AppCtx, 'teacher' | 'account'>): HTMLElement {
  const { id, set, def } = resolveAvatar(look);
  const img = h('img', { class: `pf-img ${def.fit}`, src: avatarSrc(set.id, def.name), alt: '', draggable: 'false', decoding: 'async' });
  const el = h('span', { class: `pf-avatar ${size}`, role: 'img', 'aria-label': def.label, 'data-avatar': id, style: `background:${def.bg}` }, img);
  if (ctx) txAttr(ctx, el, 'aria-label', avatarName(def));
  img.addEventListener('error', () => el.replaceChildren(def.glyph));
  return el;
}
